import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import UpdateIcon from '@mui/icons-material/Update';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import { Box, Fade, IconButton, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridColDef, GridToolbarFilterButton, useGridApiRef } from '@mui/x-data-grid-premium';
import {
  AssignedTasksDataGridColumns,
  Estate,
  EstateWorkItem_AssignedTasksDataGridPageFilter,
  EstateWorkItem_GetEstateWorkItemsByPortalId_FieldsFragment,
  EstateWorkItemSubStatus,
  useEstateNote_GetPublishedEstateNotesFromPortalQuery,
  useEstateWorkItem_GetAllEstateWorkItemsForAssignedTasksDataGridLazyQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { EstateDashboardTab } from 'pages/estate-dashboard/estate-dashboard';
import { EstateWorkItemSubStatusExternalLabel } from 'utils/estate';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import AssignedTaskDrawer from './components/assigned-task-drawer';
import AssignedTaskStatus from './components/assigned-task-status';

const useStyles = makeStyles(() => ({
  root: {
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  parentContainer: {
    color: '#1C2541',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: '24px',
  },
  subTitle: {
    fontSize: 16,
    color: '#1C2541',
  },
  dataGrid: {
    border: 0,
    borderRadius: '8px',
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
    '& .MuiDataGrid-columnHeadersInner': {
      backgroundColor: '#F9F9FB',
      borderRadius: '8px',
      borderBottom: 'none',
    },
    '& .MuiDataGrid-row': {
      borderBottom: '1px solid #ECEEF1',
      borderTop: 'none',
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      borderTop: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: 'none',
      borderTop: 'none',
    },
  },
  cellTitle: {
    fontSize: '14px',
    marginRight: '8px',
  },
  headerIcons: {
    marginRight: '8px',
  },
  headerText: {
    display: 'flex',
    alignItems: 'center',
  },
  bodyText: {
    width: '100%',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap',
    fontSize: '14px',
    lineHeight: '22px',
  },
  gridContainer: {
    marginTop: '24px',
  },
  emptyContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: '32px',
    '& img': {
      marginBottom: '16px',
    },
  },
  emptySubtitle: {
    fontSize: '14px',
  },
  lastUpdatedText: {
    color: '#596674',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  link: {
    paddingTop: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'underline',
  },
  estateWorkItemMenuIconButton: {
    textAlign: 'right',
  },
  firstPageIcon: {
    color: '#1C2541',
  },
}));

const AssignedTasksPage = () => {
  const classes = useStyles();
  const history = useHistory();

  const { portalId } = useParams<Record<string, string>>();
  const gridApiRef = useGridApiRef();

  const [currentEstateWorkItem, setCurrentEstateWorkItem] =
    useState<EstateWorkItem_GetEstateWorkItemsByPortalId_FieldsFragment | null>(null);
  const [showAssignedTaskDrawer, setShowAssignedTaskDrawer] = useState<boolean>(false);

  const [
    getAllEstateWorkItemsForAssignedTasksDataGridLazyQuery,
    getAllEstateWorkItemsForAssignedTasksDataGridLazyQueryResult,
  ] = useEstateWorkItem_GetAllEstateWorkItemsForAssignedTasksDataGridLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    variables: {
      input: {
        portalId: portalId,
        paginationInput: {
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
          pageSort: {
            column: AssignedTasksDataGridColumns.Default,
            sort: 'DESC',
          },
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate work items by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: publishedEstateNotesData, loading: publishedEstateNotesLoading } =
    useEstateNote_GetPublishedEstateNotesFromPortalQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting published estate notes from portal: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (publishedEstateNotesLoading || !publishedEstateNotesData) {
    return <Spinner />;
  }

  if (!featureSubscriptionTypeData && featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  const estateWorkItemWithNotesColumns: GridColDef<EstateWorkItem_GetEstateWorkItemsByPortalId_FieldsFragment>[] =
    [
      {
        disableColumnMenu: true,
        flex: 3,
        field: AssignedTasksDataGridColumns.WorkText,
        filterable: true,
        headerName: 'Task',
        valueGetter: ({ row }) => row.workText,
        renderHeader: () => (
          <Typography className={classes.headerText}>
            <PlaylistAddCheckIcon className={classes.headerIcons} fontSize="small" />
            Task
          </Typography>
        ),
        renderCell: ({ row }) => {
          return (
            <Box
              className={classes.bodyText}
              dangerouslySetInnerHTML={{
                __html: row?.workText || '',
              }}
            ></Box>
          );
        },
      },
      {
        disableColumnMenu: true,
        flex: 1,
        field: AssignedTasksDataGridColumns.EstateName,
        sortable: false,
        hideSortIcons: true,
        headerName: 'Estate',
        valueGetter: ({ row }) => row.estate.estateName,
        renderHeader: () => (
          <Typography className={classes.headerText}>
            <FolderSharedOutlinedIcon className={classes.headerIcons} fontSize="small" /> Estate
          </Typography>
        ),
        renderCell: ({ row }) => {
          return (
            <Link
              className={classes.link}
              onClick={() => {
                history.push(
                  `/home/${portalId}/estate/${row.estateId}/estate-dashboard?tab=${EstateDashboardTab.ESTATE_CHECKLIST}`,
                );
              }}
            >
              <Typography className={classes.bodyText}>{row.estate!.estateName}</Typography>
            </Link>
          );
        },
      },
      {
        disableColumnMenu: true,
        flex: 1,
        field: AssignedTasksDataGridColumns.Status,
        filterable: false,
        sortable: false,
        hideSortIcons: true,
        headerName: 'Status',
        valueGetter: ({ row }) => {
          const { subStatus } = row;

          return EstateWorkItemSubStatusExternalLabel[subStatus as EstateWorkItemSubStatus];
        },
        renderHeader: () => (
          <Typography className={classes.headerText}>
            <WifiTetheringIcon className={classes.headerIcons} fontSize="small" /> Status
          </Typography>
        ),
        renderCell: ({ row }) => {
          return (
            <AssignedTaskStatus
              estateWorkItemStatus={row.status}
              estateWorkItemSubStatus={row.subStatus}
              onClick={() => {
                setCurrentEstateWorkItem(row);
                setShowAssignedTaskDrawer(true);
              }}
            />
          );
        },
      },
      {
        disableColumnMenu: true,
        flex: 2,
        field: AssignedTasksDataGridColumns.Instructions,
        filterable: false,
        sortable: false,
        hideSortIcons: true,
        headerName: 'Instructions',
        valueGetter: ({ row }) => row.documentGenerationTemplate?.instructions || '',
        renderHeader: () => (
          <Typography className={classes.headerText}>
            <UpdateIcon className={classes.headerIcons} fontSize="small" />
            Instructions
          </Typography>
        ),
        renderCell: ({ row }) => {
          if (!row.documentGenerationTemplate?.instructions) {
            return '';
          }

          return (
            <Typography className={classes.bodyText}>
              {row.documentGenerationTemplate.instructions}
            </Typography>
          );
        },
      },
      {
        disableColumnMenu: true,
        field: 'estateWorkItemAssignedTaskDrawerButton',
        headerName: '',
        width: 25,
        filterable: false,
        sortable: false,
        hideSortIcons: true,
        align: 'center',
        renderCell: ({ row }) => {
          return (
            <IconButton
              className={classes.estateWorkItemMenuIconButton}
              id={`estate-work-item-assigned-task-drawer-button-${row.id}`}
              onClick={() => {
                setCurrentEstateWorkItem(row);
                setShowAssignedTaskDrawer(true);
              }}
            >
              <FirstPageIcon className={classes.firstPageIcon} />
            </IconButton>
          );
        },
      },
    ];

  const getAssignedTaskGridToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  return (
    <>
      <HomeLayout>
        <Fade in={true}>
          <div className={classes.parentContainer}>
            <Typography className={classes.title}>Assigned Tasks</Typography>
            <Typography className={classes.subTitle}>
              Our team of professionals will manage tasks for your estates, so you can focus on what
              you do best.
            </Typography>
            <div className={classes.gridContainer}>
              <ServerEnabledDataGrid<EstateWorkItem_AssignedTasksDataGridPageFilter>
                apiRef={gridApiRef}
                className={classes.dataGrid}
                loading={getAllEstateWorkItemsForAssignedTasksDataGridLazyQueryResult.loading}
                gridStateKey={GridStateKey.AssignedTasksDataGrid}
                columns={estateWorkItemWithNotesColumns}
                queryResult={getAllEstateWorkItemsForAssignedTasksDataGridLazyQueryResult}
                executeLazyQuery={getAllEstateWorkItemsForAssignedTasksDataGridLazyQuery}
                disableColumnReorder
                disableRowSelectionOnClick
                getRowHeight={() => 'auto'}
                autoHeight={true}
                rowHeight={70}
                columnHeaderHeight={35}
                pagination
                slots={{
                  toolbar: getAssignedTaskGridToolbar,
                }}
              />
            </div>
          </div>
        </Fade>
      </HomeLayout>
      {currentEstateWorkItem && showAssignedTaskDrawer && (
        <AssignedTaskDrawer
          estateWorkItemId={currentEstateWorkItem.id}
          estate={currentEstateWorkItem.estate! as Estate}
          onClose={() => setShowAssignedTaskDrawer(false)}
        />
      )}
    </>
  );
};

export default AssignedTasksPage;
